/*
Shared empty-state component — illustration + title + body + optional CTA.

Mirrors the Android `illus_empty_*.xml` drawable family: same canopée
silhouette path (viewport 160), muted at 40 % alpha so the empty state
reads as a "not yet" rather than a "missing data" warning. Each
variant overlays a small accent that hints at the screen's purpose
(seedling, orange remarquable pastille, badge ribbon).

Returns an HTML string the caller can drop into its DOM. The CTA is
optional and rendered as a `<a class="empty-cta">` so callers wire
its href / click however they like (or omit it for view-only screens
like the Arboretum-empty list).
*/

export type EmptyStateVariant = 'arboretum' | 'remarquables' | 'profile' | 'badges'

export interface EmptyStateOptions
{
	variant: EmptyStateVariant
	title: string
	body: string
	ctaLabel?: string  // omit to render no CTA
	ctaHref?: string   // hash route or external href; combined with ctaLabel
	ctaId?: string     // optional id on the CTA element so callers can wire `addEventListener` directly
}

/*
Render the empty-state markup. Caller is expected to inject the
returned string into a container that has the `.screen-empty-host`
shape (or any block that lays out a column of children) so the
illustration + text stack centers nicely.
*/
export function renderEmptyState(inOpts: EmptyStateOptions): string
{
	const vSvg = renderIllustration(inOpts.variant)
	const vCta = inOpts.ctaLabel
		? `<a class="empty-cta" ${inOpts.ctaHref ? `href="${escapeAttr(inOpts.ctaHref)}"` : ''} ${inOpts.ctaId ? `id="${escapeAttr(inOpts.ctaId)}"` : ''}>${escapeHtml(inOpts.ctaLabel)}</a>`
		: ''
	return `
		<div class="empty-state" data-variant="${inOpts.variant}">
			<div class="empty-state-illus" aria-hidden="true">${vSvg}</div>
			<h2 class="display empty-state-title">${escapeHtml(inOpts.title)}</h2>
			<p class="empty-state-body">${escapeHtml(inOpts.body)}</p>
			${vCta}
		</div>
	`
}

/*
Inline SVG for each variant. The base canopée path comes verbatim from
`app/src/main/res/drawable/illus_empty_arboretum.xml` (same
viewport-160 path used by all three Android empty drawables); accents
are added per-variant. Embedding inline keeps the build stdlib-only —
no extra asset request, no flash of missing illustration on cold load.
*/
function renderIllustration(inVariant: EmptyStateVariant): string
{
	const kCanopeePath = 'M71.1,133.3 L71.1,97.8 C56.3,97.8 44.4,91.9 38.5,80 C32.6,77 32.6,65.2 35.6,56.3 C32.6,44.4 41.5,32.6 53.3,32.6 C56.3,23.7 68.1,17.8 80,17.8 C91.9,17.8 103.7,23.7 106.7,32.6 C118.5,32.6 127.4,44.4 124.4,56.3 C127.4,65.2 127.4,77 121.5,80 C115.6,91.9 103.7,97.8 88.9,97.8 L88.9,133.3 Z'

	if (inVariant === 'remarquables')
	{
		// Same canopée + orange remarquable pastille at lower-right with a
		// mini cream-fill canopée inside it. Verbatim from the Android
		// `illus_empty_remarquables.xml` drawable.
		return `
			<svg viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg" role="img">
				<path d="${kCanopeePath}" fill="#7A8F6E" fill-opacity="0.40"/>
				<path d="M120,92 A24,24 0 1 1 120,140 A24,24 0 1 1 120,92 Z" fill="#FB8C00"/>
				<path d="M117,132 L117,123 C112,123 108,121 106,116 C103,114 103,110 105,107 C103,103 105,99 109,99 C110,96 113,94 120,94 C127,94 129,96 130,99 C134,99 137,103 134,107 C137,110 137,114 134,116 C132,121 128,123 123,123 L123,132 Z" fill="#F5F1E6"/>
			</svg>
		`
	}

	if (inVariant === 'badges')
	{
		// Canopée + a small medallion at lower-right. Geometrically
		// distinct from the remarquable pastille so the screens read
		// differently at a glance.
		return `
			<svg viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg" role="img">
				<path d="${kCanopeePath}" fill="#7A8F6E" fill-opacity="0.40"/>
				<circle cx="120" cy="116" r="22" fill="#C8A24A"/>
				<circle cx="120" cy="116" r="14" fill="#F5F1E6"/>
				<path d="M120 109 L122 114 L127 114 L123 117 L125 122 L120 119 L115 122 L117 117 L113 114 L118 114 Z" fill="#C8A24A"/>
			</svg>
		`
	}

	// 'arboretum' and 'profile' both use the bare muted silhouette —
	// the Android equivalents (`illus_empty_arboretum.xml` and
	// `illus_empty_profile.xml`) are byte-identical for the same
	// reason.
	return `
		<svg viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg" role="img">
			<path d="${kCanopeePath}" fill="#7A8F6E" fill-opacity="0.40"/>
		</svg>
	`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function escapeAttr(inText: string): string { return escapeHtml(inText) }
